import React, { useState, useEffect } from 'react';
import { Tooltip, IconButton, Box } from '@mui/material';
import { useThemeColors } from '../../../utils/useThemeColors';

const SmallFloatingToolbarMenu = ({
  isViewDetailsVisible,
  toggleViewDetails,
  displayPrices,
  togglePrices,
  isLoading,
  handlePdfDownload,
  handleSendEmail,
  handleSaveQuote,
  flipped,
  handleFlipToggle,
  handleZoomIn,
  handleZoomOut,
  showOriginalToolbar,
  toggleOriginalToolbar,
  hideFlip = false // products without an inside/outside view (pleated screens)
}) => {
  const [position, setPosition] = useState({ x: 27, y: Math.round(window.innerHeight / 2) - 300 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const tc = useThemeColors();

  const handleMouseDown = (e) => {
    if (e.target.closest('.toolbar-button')) return;
    setIsDragging(true);
    setDragOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragOffset.x,
      y: e.clientY - dragOffset.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  const menuItems = [
    { id: 'viewDetails', icon: isViewDetailsVisible ? '👁️' : '👁️‍🗨️', tooltip: isViewDetailsVisible ? 'Hide Details' : 'View Details', onClick: toggleViewDetails, disabled: false },
    { id: 'prices', icon: displayPrices ? '💰' : '🏷️', tooltip: displayPrices ? 'Hide Prices' : 'Show Prices', onClick: togglePrices, disabled: !isViewDetailsVisible },
    { id: 'pdf', icon: '📄', tooltip: 'Download PDF', onClick: handlePdfDownload, disabled: !isViewDetailsVisible || isLoading, loading: isLoading },
    { id: 'email', icon: '✉️', tooltip: 'Send Email', onClick: () => handleSendEmail(true), disabled: !isViewDetailsVisible },
    { id: 'save', icon: '💾', tooltip: 'Save', onClick: () => handleSaveQuote(false), disabled: false },
    { id: 'exit', icon: '↩️', tooltip: 'Save & Exit', onClick: () => handleSaveQuote(true), disabled: false },
    { id: 'flip', icon: flipped ? '🔙' : '🔄', tooltip: flipped ? 'Show Front' : 'Show Back', onClick: handleFlipToggle, disabled: false },
    { id: 'zoomIn', icon: '🔍+', tooltip: 'Zoom In', onClick: handleZoomIn, disabled: false },
    { id: 'zoomOut', icon: '🔍−', tooltip: 'Zoom Out', onClick: handleZoomOut, disabled: false },
    { id: 'toggleToolbar', icon: showOriginalToolbar ? '📌' : '📍', tooltip: showOriginalToolbar ? 'Hide Original Toolbar' : 'Show Original Toolbar', onClick: toggleOriginalToolbar, disabled: false },
  ].filter(item => !(hideFlip && item.id === 'flip'));

  return (
    <Box
      onMouseDown={handleMouseDown}
      sx={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        zIndex: 9999,
        backgroundColor: tc.panelBg,
        border: `1px solid ${tc.panelBorder}`,
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        padding: '4px',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '2px',
        width: 'fit-content',
      }}
    >
      {menuItems.map((item) => (
        <Tooltip
          key={item.id}
          title={item.tooltip}
          arrow
          placement="top"
          disableInteractive
          slotProps={{
            popper: {
              sx: { zIndex: 10000, pointerEvents: 'none' }
            }
          }}
        >
          <span>
            <IconButton
              className="toolbar-button"
              onClick={item.onClick}
              disabled={item.disabled}
              sx={{
                width: 32,
                height: 32,
                fontSize: '14px',
                padding: 0,
                borderRadius: '4px',
                backgroundColor: item.disabled ? tc.buttonBgDisabled : tc.headerBg,
                opacity: item.disabled ? 0.5 : 1,
                '&:hover': {
                  backgroundColor: item.disabled ? tc.buttonHoverDisabled : tc.headerHover,
                },
              }}
            >
              {item.loading ? '⏳' : item.icon}
            </IconButton>
          </span>
        </Tooltip>
      ))}
    </Box>
  );
};

export default SmallFloatingToolbarMenu;
